import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button, Label } from "@/components/ui/primitives";
import { toast } from "@/components/ui/toaster";
import { attachCapability } from "@/lib/api/endpoints";
import { useCapabilities } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";

const selectClass =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

/** Attach an existing capability to a version. `attachedToolNames` are hidden. */
export function AttachCapabilityDialog({
  open,
  onOpenChange,
  agentSlug,
  label,
  attachedToolNames,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agentSlug: string;
  label: string;
  attachedToolNames: string[];
}) {
  const qc = useQueryClient();
  const caps = useCapabilities();
  const available = (caps.data ?? []).filter((c) => !attachedToolNames.includes(c.tool_name));

  const [toolName, setToolName] = React.useState("");
  const [requiresApproval, setRequiresApproval] = React.useState(false);

  // A tool that writes or reaches outside starts with approval on; the author
  // can still turn it off (AVI-163 #23).
  const pick = (name: string) => {
    setToolName(name);
    const tier = available.find((c) => c.tool_name === name)?.risk_tier;
    setRequiresApproval(tier === "write" || tier === "external");
  };

  React.useEffect(() => {
    if (open) pick(available[0]?.tool_name ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, caps.data]);

  const mutation = useMutation({
    mutationFn: () =>
      attachCapability(agentSlug, label, {
        tool_name: toolName,
        requires_approval: requiresApproval,
      }),
    onSuccess: async () => {
      toast.success("Tool attached.");
      await qc.invalidateQueries({ queryKey: qk.attached(agentSlug, label) });
      await qc.invalidateQueries({ queryKey: qk.version(agentSlug, label) });
      await qc.invalidateQueries({ queryKey: qk.capabilities });
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Attach tool</DialogTitle>
          <DialogDescription>Give this version access to a capability.</DialogDescription>
        </DialogHeader>
        {available.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No unattached tools available. Create one under Tools first.
          </p>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (toolName) mutation.mutate();
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="at-tool">Tool</Label>
              <select
                id="at-tool"
                className={selectClass}
                value={toolName}
                onChange={(e) => pick(e.target.value)}
              >
                {available.map((c) => (
                  <option key={c.tool_name} value={c.tool_name}>
                    {c.name} ({c.execution_type})
                  </option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-input accent-primary"
                checked={requiresApproval}
                onChange={(e) => setRequiresApproval(e.target.checked)}
              />
              Requires approval before each call
            </label>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!toolName || mutation.isPending}>
                {mutation.isPending ? "Attaching…" : "Attach"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
